import React, { FunctionComponent } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useWillUnmount } from 'rooks'
import { NativeUpgradeRequiredAlert, messages } from '@mobi/account/Areas/Deposit/Components'
import {
  ApplePayButtonNative,
  ApplePayButtonWeb,
} from '@mobi/account/Areas/Deposit/Components/Payments/ApplePay'
import { BankCardButton } from '@mobi/account/Areas/Deposit/Components/Payments/BankCard'
import {
  GooglePayButtonNative,
  GooglePayButtonWeb,
} from '@mobi/account/Areas/Deposit/Components/Payments/GooglePay'
import {
  PayPalButtonNative,
  PayPalButtonWeb,
} from '@mobi/account/Areas/Deposit/Components/Payments/PayPal'
import type { OnFailureParams, PanelProps } from '@mobi/account/Areas/Deposit/Components/types'
import { incrementSuccessCount, paymentMethodTypeNameMap } from '@mobi/account/Areas/Deposit/Utils'
import { isNativeBraintreeAvailable } from '@mobi/account/Areas/Deposit/Utils/isNativeBraintreeAvailable'
import { PaymentLimitHelpText } from '@mobi/account/Areas/DepositLimit/Utils/PaymentLimitHelpText'
import { useBetAccount } from '@mobi/account/Hooks'
import type { InitialData, PaymentMethod } from '@mobi/api-types'
import type { PaymentMethods } from '@mobi/component-library/Areas/Deposit/types'
import { SpinnerStandalone } from '@mobi/component-library/Legacy/Spinner'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import { addCrumb } from '@mobi/utils'
import { toMoney } from '@mobi/utils/money'
import { isReactNativeApp as isNative, isReactNativeApp } from '@mobi/web-native-comms/web'
import { PaymentAvailabilities } from '@core/Areas/Deposit/helper'
import {
  LoadingContainerStyled,
  QuickDepositBodyContainerStyled,
  QuickDepositBodySectionStyled,
  QuickDepositLabelStyled,
} from '@core/Areas/QuickDeposit/Components/QuickDeposit.styles'
import {
  clearQuickDepositError,
  onQuickDepositError,
  setQuickDepositStatus,
} from '@core/Areas/QuickDeposit/Store'
import { selectLastDepositError } from '@core/Areas/QuickDeposit/Store/selectors'
import {
  trackQuickDepositCancelled,
  trackQuickDepositFailed,
  trackQuickDepositStarted,
  trackQuickDepositSucceeded,
} from '@core/Areas/QuickDeposit/analytics'
import { QUICKBET_MODAL_ID } from '@core/Areas/Quickbet/constants'
import { ClearInsufficientFundsForBet } from '@core/Areas/Quickbet/signals'
import { allowDismissal, preventDismissal } from '@core/Components/Modal/store'
import { NoticeBox } from '@core/Components/NoticeBox'
import { Text } from '@core/Components/Text'
import { Money } from '@core/Components/Text/Money'
import { fetchAccountBalanceAsync } from '@core/State/UserAccount/async-signals'
import { useFeature } from '@core/Utils/hooks'

type Props = {
  lastUsedPaymentMethod: PaymentMethods
  creditCard?: PaymentMethod
  willDepositMinimumAmount: boolean
  minimumDepositAmount: number
  depositAmount: number
  initialData: InitialData
  isBusy: boolean
}

export const QuickDepositBody = ({
  lastUsedPaymentMethod,
  creditCard,
  willDepositMinimumAmount,
  minimumDepositAmount,
  depositAmount,
  initialData,
  isBusy,
}: Props) => {
  const dispatch = useDispatch()
  const lastQuickDepositError = useSelector(selectLastDepositError)
  const isDepositValueVolumeLimitsEnabled = useFeature(FeatureFlags.DEPOSIT_VALUE_VOLUME_LIMITS.key)

  const { isLoading, isError, data: betAccount } = useBetAccount()

  const paymentAvailabilities = PaymentAvailabilities(betAccount)

  const isDepositLimitReached =
    isDepositValueVolumeLimitsEnabled &&
    !!betAccount &&
    (betAccount?.RemainingMaxDepositAmount <= 0 || betAccount?.RemainingDepositTransactions <= 0)

  const onStart = () => {
    trackQuickDepositStarted(lastUsedPaymentMethod)
    addCrumb(
      'deposit',
      `start deposit with ${
        paymentMethodTypeNameMap[lastUsedPaymentMethod]
      } for ${depositAmount.toFixed(2)}`
    )

    dispatch(preventDismissal(QUICKBET_MODAL_ID))
    dispatch(clearQuickDepositError())
    dispatch(
      setQuickDepositStatus({
        depositStatus: 'preparing',
      })
    )
  }

  const onDepositing = () => {
    dispatch(
      setQuickDepositStatus({
        depositStatus: 'depositing',
      })
    )
  }

  const onSuccess = async (amount: number): Promise<void> => {
    if (betAccount == null) return

    const successfulDepositCount = incrementSuccessCount(betAccount.BetAccountNumber)

    trackQuickDepositSucceeded({
      depositAmount: amount.toFixed(2),
      paymentMethod: lastUsedPaymentMethod,
      accountNumber: betAccount.BetAccountNumber,
      isFirstDeposit: successfulDepositCount === 1,
      isSecondDeposit: successfulDepositCount === 2,
    })
    addCrumb('deposit', `successful deposit`)

    await fetchAccountBalanceAsync()
    ClearInsufficientFundsForBet() // close Quick Deposit
  }

  const onFailure = (args: OnFailureParams) => {
    onSettled()

    trackQuickDepositFailed(lastUsedPaymentMethod, args.reason)

    dispatch(onQuickDepositError(messages[args.reason]))
  }

  const onCancel = () => {
    trackQuickDepositCancelled(lastUsedPaymentMethod)
    addCrumb('deposit', `cancel deposit`)
    onSettled()
  }

  const onSettled = () => {
    dispatch(allowDismissal(QUICKBET_MODAL_ID))
    dispatch(
      setQuickDepositStatus({
        depositStatus: 'idle',
      })
    )
  }

  useWillUnmount(onSettled)

  const renderPaymentMethod = () => {
    if (betAccount == null) return null

    const panelProps: PanelProps = {
      initialData,
      depositAmount,
      isDepositAllowed: !isBusy && !isDepositLimitReached,
      onStart,
      onSuccess,
      onDepositing,
      onFailure,
      onCancel,
      accountNumber: betAccount.BetAccountNumber,
    }

    if (lastUsedPaymentMethod === 'CreditCard') {
      if (creditCard) {
        return <BankCardButton {...panelProps} creditCard={creditCard} showIssuer={true} />
      }
      // User attempts to do a credit card deposit without a credit card
      // While this isn't theoretically possible, we do need to handle it
      return null
    }

    const Component = determineComponent(lastUsedPaymentMethod, isNative)

    return <Component {...panelProps} />
  }

  return (
    <QuickDepositBodyContainerStyled style={{ display: isBusy ? 'none' : undefined }}>
      {lastQuickDepositError && (
        <NoticeBox
          title='Deposit failed'
          noticeBoxType='Error'
          hasBorder
          role='alert'
          aria-label='Deposit failed'
        >
          <Text size='lg'>{lastQuickDepositError}</Text>
        </NoticeBox>
      )}

      {(function () {
        if (!betAccount || isLoading) {
          return (
            <LoadingContainerStyled>
              <SpinnerStandalone />
            </LoadingContainerStyled>
          )
        }
        if (isError) {
          return (
            <p>Unable to deposit funds. No previous payment method has been used on this device.</p>
          )
        }
        if (paymentAvailabilities[lastUsedPaymentMethod].blocked) {
          return (
            <p>
              Deposits are not enabled for {paymentMethodTypeNameMap[lastUsedPaymentMethod]} on this
              account.
            </p>
          )
        }

        if (
          isReactNativeApp &&
          ['ApplePay', 'PayPal'].includes(lastUsedPaymentMethod) &&
          !isNativeBraintreeAvailable()
        )
          return <NativeUpgradeRequiredAlert />

        return (
          <QuickDepositBodySectionStyled>
            <header>
              <QuickDepositLabelStyled size='lg'>
                <strong>Would you like to make a quick deposit now?</strong>
              </QuickDepositLabelStyled>
            </header>
            <div>
              <QuickDepositLabelStyled size='lg'>
                <strong>
                  Deposit <Money amount={depositAmount} /> using
                </strong>
              </QuickDepositLabelStyled>

              {isBusy && (
                <LoadingContainerStyled>
                  <SpinnerStandalone />
                </LoadingContainerStyled>
              )}

              {renderPaymentMethod()}

              {isDepositValueVolumeLimitsEnabled && !!betAccount && (
                <PaymentLimitHelpText
                  paymentType='deposit'
                  inputValue={depositAmount}
                  paymentLimits={initialData.depositLimits}
                  remainingPaymentAmount={betAccount.RemainingMaxDepositAmount}
                  remainingPaymentTransactions={betAccount.RemainingDepositTransactions}
                  isInfoIconShown={false}
                />
              )}

              {willDepositMinimumAmount && (
                <QuickDepositLabelStyled
                  role='status'
                  aria-label={`Minimum deposit is ${toMoney(minimumDepositAmount)}`}
                >
                  Minimum deposit is {toMoney(minimumDepositAmount, { decimalPlaces: 0 })}
                </QuickDepositLabelStyled>
              )}
            </div>
          </QuickDepositBodySectionStyled>
        )
      })()}
    </QuickDepositBodyContainerStyled>
  )
}

function determineComponent(
  paymentMethod: Exclude<PaymentMethods, 'CreditCard'>,
  isNative: boolean
): FunctionComponent<PanelProps> {
  switch (paymentMethod) {
    case 'ApplePay':
      return isNative ? ApplePayButtonNative : ApplePayButtonWeb
    case 'GooglePay':
      return isNative ? GooglePayButtonNative : GooglePayButtonWeb
    case 'PayPal':
      return isNative ? PayPalButtonNative : PayPalButtonWeb
  }
}
